type I18nKey = keyof typeof import("@/i18n/en").dict

export const progressActions = {
  reading: "cowork.progress.reading",
  extracting: "cowork.progress.extracting",
  checking: "cowork.progress.checking",
  calculating: "cowork.progress.calculating",
  generating: "cowork.progress.generating",
  saving: "cowork.progress.saving",
} as const satisfies Record<string, I18nKey>

export const progressObjects = {
  document: "cowork.progress.document",
  table: "cowork.progress.table",
  dataset: "cowork.progress.dataset",
  report: "cowork.progress.report",
} as const satisfies Record<string, I18nKey>

export const progressNamedObjects = {
  document: "cowork.progress.namedDocument",
  table: "cowork.progress.namedTable",
  dataset: "cowork.progress.namedDataset",
  report: "cowork.progress.namedReport",
} as const satisfies Record<string, I18nKey>

export const progressCounts = {
  rows: { known: "cowork.progress.rowsTotal", unknown: "cowork.progress.rows" },
  documents: { known: "cowork.progress.documentsTotal", unknown: "cowork.progress.documents" },
  items: { known: "cowork.progress.itemsTotal", unknown: "cowork.progress.items" },
} as const satisfies Record<string, { known: I18nKey; unknown: I18nKey }>

export type OperationProgress = {
  schema_name: "andmera.operation_progress"
  schema_version: 1
  operation_id: string
  action: keyof typeof progressActions
  object_type: keyof typeof progressObjects
  status: "pending" | "running" | "completed" | "failed"
  object_name?: string
  section_name?: string
  processed?: number
  total?: number
  unit?: keyof typeof progressCounts
}

const fields = new Set([
  "schema_name",
  "schema_version",
  "operation_id",
  "action",
  "object_type",
  "status",
  "object_name",
  "section_name",
  "processed",
  "total",
  "unit",
])

function displayName(value: unknown) {
  if (typeof value !== "string" || !value.trim() || value.length > 160) return
  if (/^[./\\]|^[a-z]:|:\/\/|[\\<>\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/i.test(value)) return
  if (value.includes("/") && !value.includes(" / ")) return
  if (/tool-output|node_modules|credentials?|secrets?|password|private[-_]key|token|workspace\//i.test(value)) return
  if (/^[\da-f-]{32,}(?:\.[a-z]+)?$/i.test(value)) return
  return value
}

const count = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0

export function parseOperationProgress(value: unknown): OperationProgress | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return
  const row = value as Record<string, unknown>
  if (Object.keys(row).some((key) => !fields.has(key))) return
  if (row.schema_name !== "andmera.operation_progress" || row.schema_version !== 1) return
  if (typeof row.operation_id !== "string" || !row.operation_id || row.operation_id.length > 512) return
  if (typeof row.action !== "string" || !Object.hasOwn(progressActions, row.action)) return
  if (typeof row.object_type !== "string" || !Object.hasOwn(progressObjects, row.object_type)) return
  if (!["pending", "running", "completed", "failed"].includes(row.status as string)) return
  if (row.processed !== undefined) {
    if (!count(row.processed) || typeof row.unit !== "string" || !Object.hasOwn(progressCounts, row.unit)) return
    if (row.total !== undefined && (!count(row.total) || row.total < row.processed)) return
  } else if (row.total !== undefined || row.unit !== undefined) return
  const objectName = displayName(row.object_name)
  const sectionName = displayName(row.section_name)
  return {
    schema_name: "andmera.operation_progress",
    schema_version: 1,
    operation_id: row.operation_id,
    action: row.action as OperationProgress["action"],
    object_type: row.object_type as OperationProgress["object_type"],
    status: row.status as OperationProgress["status"],
    ...(objectName ? { object_name: objectName } : {}),
    ...(sectionName ? { section_name: sectionName } : {}),
    ...(row.processed !== undefined
      ? { processed: row.processed as number, unit: row.unit as OperationProgress["unit"] }
      : {}),
    ...(row.total !== undefined ? { total: row.total as number } : {}),
  }
}

export function describeProgress(
  progress: OperationProgress,
  t: (key: I18nKey, params?: Record<string, string>) => string,
) {
  const lines = [
    t(progressActions[progress.action]),
    progress.object_name
      ? t(progressNamedObjects[progress.object_type], { name: progress.object_name })
      : t(progressObjects[progress.object_type]),
  ]
  if (progress.section_name) lines.push(t("cowork.progress.section", { name: progress.section_name }))
  if (progress.processed !== undefined && progress.unit) {
    const keys = progressCounts[progress.unit]
    lines.push(
      progress.total === undefined
        ? t(keys.unknown, { processed: String(progress.processed) })
        : t(keys.known, { processed: String(progress.processed), total: String(progress.total) }),
    )
  }
  return lines
}
